<template>
  <div class="analytics-page space-y-5 stagger-children">
    <!-- Error Banner -->
    <AlertBanner v-if="error" type="error" :message="error" @dismiss="error = null">
      <template #action>
        <button
          @click="loadData"
          class="text-sm font-semibold text-red-600 dark:text-red-400 hover:underline"
        >
          Retry
        </button>
      </template>
    </AlertBanner>

    <!-- Breadcrumb + Page Header -->
    <div class="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
      <!-- shrink-0: the toolbar wraps before the breadcrumb or title does -->
      <div class="shrink-0 min-w-0">
        <nav class="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 mb-1 whitespace-nowrap" aria-label="Breadcrumb">
          <router-link
            :to="{ name: 'AnalyticsDashboard' }"
            class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium"
          >
            Analytics
          </router-link>
          <span class="material-icons text-[14px]" aria-hidden="true">chevron_right</span>
          <span class="text-neutral-700 dark:text-neutral-300 font-medium" aria-current="page">Supplier</span>
        </nav>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          {{ supplierName }}
        </h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span class="font-medium text-neutral-700 dark:text-neutral-300">{{ periodLabel }}</span>
          <template v-if="summary">
            <span aria-hidden="true">·</span>
            <span>{{ summary.totalOrders?.toLocaleString() }} orders</span>
          </template>
          <span v-else class="inline-block h-4 w-20 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse align-middle" aria-hidden="true"></span>
        </p>
      </div>

      <AnalyticsToolbar
        v-model:start-date="startDate"
        v-model:end-date="endDate"
        :max-date="maxDate"
        :active-toggle="activeToggle"
        :quick-toggles="quickToggles"
        :downloading="downloading"
        @apply-range="applyQuickRange"
        @download="handleDownload"
      />
    </div>

    <!-- KPI Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label="Total Actual Volume"
        :value="summary?.totalActualVolume ?? null"
        unit="L"
        icon="local_gas_station"
        color="primary"
        :decimals="0"
        :loading="loading && !summary"
        hero
        :sparkline="volSpark"
      />
      <KpiCard
        label="Completed Orders"
        :value="summary?.completedOrders ?? null"
        :unit="`of ${formatNumber(summary?.totalOrders)}`"
        icon="check_circle"
        color="emerald"
        :decimals="0"
        :loading="loading && !summary"
        :sparkline="completedSpark"
      />
      <KpiCard
        label="Locations"
        :value="locations.length"
        unit="locations"
        icon="place"
        color="secondary"
        :decimals="0"
        :loading="loading && !supplierDetail"
      />
      <KpiCard
        label="Completion Rate"
        :value="summary?.completionRate ?? null"
        unit="%"
        icon="task_alt"
        color="amber"
        :decimals="1"
        :loading="loading && !summary"
        :sparkline="rateSpark"
      />
    </div>

    <!-- Trend beside the location ranking — same row structure as the dashboard and group pages -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
      <div :class="locations.length > 1 ? 'lg:col-span-2' : 'lg:col-span-3'">
        <ChartCard
          title="Volume Trend"
          subtitle="Actual litres collected per week"
          fill
          :loading="loading && !supplierDetail"
          :is-empty="!hasTrend"
          empty-icon="show_chart"
          empty-text="No trend data available"
          aria-label="Weekly actual volume trend line chart"
        >
          <Line :data="trendData" :options="trendOptions" />
          <template v-if="partialWeeks.length" #footer>
            <span class="inline-flex items-center gap-1">
              <span class="inline-block w-4 border-t-2 border-dashed border-neutral-400 dark:border-neutral-500" aria-hidden="true"></span>
              * partial week — fewer than 7 days of the selected period fall in it ({{ partialWeekSummary }}); hover for details
            </span>
          </template>
        </ChartCard>
      </div>

      <!-- Single-location suppliers have nothing to rank; the chart takes the full row instead -->
      <RankedBarList
        v-if="locations.length > 1"
        title="Locations"
        subtitle="Actual volume, ranked"
        :items="locationItems"
        :loading="loading && !supplierDetail"
        :max-visible="8"
        items-label="locations"
        empty-icon="place"
        empty-text="No location data"
      />
    </div>

    <!-- Location Breakdown Table -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
      <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Location Breakdown</h2>
      <DataTable
        :data="locationTableData"
        :columns="locationColumns"
        :loading="loading"
        :pagination="true"
        :searchable="true"
        row-key="locationId"
        loading-text="Loading location data..."
        empty-text="No location data available for this period"
      >
        <!-- Location Name -->
        <template #cell-locationName="{ row }">
          <div class="flex items-center gap-2">
            <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
              <span class="text-xs font-bold text-primary-700 dark:text-primary-300">
                {{ row.locationName?.charAt(0)?.toUpperCase() }}
              </span>
            </div>
            <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {{ row.locationName }}
            </span>
          </div>
        </template>

        <!-- Actual Volume -->
        <template #cell-actualVolume="{ value }">
          <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Orders -->
        <template #cell-orders="{ value }">
          <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Completed -->
        <template #cell-completed="{ value }">
          <span class="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">
            {{ formatNumber(value) }}
          </span>
        </template>

        <!-- Rate -->
        <template #cell-rate="{ value }">
          <div class="flex items-center gap-2">
            <div class="flex-1 h-1.5 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden max-w-[60px]">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="value >= 90 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-500'"
                :style="{ width: (value || 0) + '%' }"
              ></div>
            </div>
            <!-- Non-color cue for the good/fair/low band (bar hue alone is invisible to colorblind users) -->
            <span class="sr-only">{{ value >= 90 ? 'Good' : value >= 70 ? 'Fair' : 'Low' }} completion:</span>
            <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {{ value != null ? value.toFixed(1) : '--' }}%
            </span>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Order History Table -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
      <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Order History</h2>
      <DataTable
        :data="orderTableData"
        :columns="orderColumns"
        :loading="loading"
        :pagination="true"
        :searchable="true"
        row-key="id"
        loading-text="Loading order history..."
        empty-text="No orders found for this period"
      >
        <!-- Order ID -->
        <template #cell-id="{ value }">
          <span class="text-sm font-mono font-medium text-neutral-900 dark:text-neutral-100">
            {{ value }}
          </span>
        </template>

        <!-- Requested Date -->
        <template #cell-requestedDate="{ value }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">
            {{ formatDate(value) }}
          </span>
        </template>

        <!-- Location Name -->
        <template #cell-locationName="{ value }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">
            {{ value || '--' }}
          </span>
        </template>

        <!-- Status Badge -->
        <template #cell-status="{ value }">
          <span :class="['inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold', statusBadgeClass(value)]">
            {{ value }}
          </span>
        </template>

        <!-- Actual Volume -->
        <template #cell-actualVolume="{ value }">
          <span class="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
            {{ formatNumber(value) }}
          </span>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useAnalytics } from '@/composables/useAnalytics'
import useToast from '@/composables/useToast'
import { useDateRange } from '@/composables/useDateRange'
import AnalyticsToolbar from '@/components/analytics/AnalyticsToolbar.vue'
import RankedBarList from '@/components/analytics/RankedBarList.vue'
import { useChartTheme } from '@/composables/useChartTheme'
import { useWeeklyTrend } from '@/composables/useWeeklyTrend'
import { formatNumber, formatDate, formatPeriodLabel } from '@/utils/format'
import DataTable from '@/components/ui/DataTable.vue'
import KpiCard from '@/components/analytics/KpiCard.vue'
import ChartCard from '@/components/analytics/ChartCard.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip)

export default {
  name: 'AnalyticsSupplierView',
  components: {
    AnalyticsToolbar,
    RankedBarList,
    DataTable,
    KpiCard,
    ChartCard,
    AlertBanner,
    Line,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { showToast } = useToast()
    const {
      supplierDetail, loading, error,
      fetchSupplierDetail, downloadReport,
    } = useAnalytics()

    const { theme, chartColors } = useChartTheme()

    const loadData = async () => {
      await fetchSupplierDetail(props.id, startDate.value, endDate.value)
    }

    // Keep the selected period in the URL (?start/&end) so the range survives
    // back-navigation and is inherited from the dashboard drill-down.
    const syncRangeToQuery = () => {
      router.replace({ query: { ...route.query, start: startDate.value, end: endDate.value } })
    }

    let skipNextRangeFetch = false
    function onRangeChange() {
      syncRangeToQuery()
      if (skipNextRangeFetch) {
        // Initial query-param adoption; onMounted performs the first fetch.
        skipNextRangeFetch = false
        return
      }
      loadData()
    }

    const { startDate, endDate, activeToggle, quickToggles, applyQuickRange, maxDate }
      = useDateRange(onRangeChange)

    if (
      route.query.start && route.query.end &&
      route.query.start <= route.query.end && // ignore an inverted/malformed range
      (route.query.start !== startDate.value || route.query.end !== endDate.value)
    ) {
      skipNextRangeFetch = true
      startDate.value = String(route.query.start)
      endDate.value = String(route.query.end)
    }

    onMounted(() => {
      store.commit('setCurrentRoute', 'Analytics')
      loadData()
    })

    // PDF/CSV export — same scope/scopeId convention as AnalyticsReports.
    const downloading = ref(null) // 'pdf' | 'csv' while a report generates server-side

    const handleDownload = async (format) => {
      if (downloading.value) return
      downloading.value = format
      error.value = null
      try {
        await downloadReport(store.state.currentSite, startDate.value, endDate.value, 'individual', String(props.id), format)
        // downloadReport never throws — failures surface via the shared error ref.
        if (error.value) {
          error.value = null // the banner's Retry reloads page data, wrong for a download
          showToast('error', 'Failed to download report. Please try again.')
        } else {
          showToast('success', 'Report downloaded')
        }
      } finally {
        downloading.value = null
      }
    }

    // Derived data from supplierDetail
    const summary = computed(() => supplierDetail.value?.summary || null)
    const locations = computed(() => supplierDetail.value?.locations || [])
    const orders = computed(() => supplierDetail.value?.orders || [])

    const supplierName = computed(() => {
      return supplierDetail.value?.supplierName || summary.value?.supplierName || `Supplier #${props.id}`
    })

    // Weekly trend points — folded into the detail call as a bare array (get_supplier_detail
    // returns `trends: _bucket_weekly(orders)`, i.e. the weekly list directly).
    // >=1 with a visible point, matching the dashboard's threshold — a single
    // week renders as a dot instead of a misleading "No trend data" empty state.
    const weekly = computed(() => supplierDetail.value?.trends || [])

    const sparkFrom = (key) => {
      const arr = weekly.value.map((w) => w[key] ?? 0)
      return arr.length >= 2 ? arr : null
    }
    const volSpark = computed(() => sparkFrom('actualVolume'))
    const completedSpark = computed(() => sparkFrom('completedCount'))
    const rateSpark = computed(() => sparkFrom('completionRate'))

    // Plain-language period for the header
    const periodLabel = computed(() => formatPeriodLabel(startDate.value, endDate.value))

    // Volume trend — shared weekly model (date labels, partial-week marking, brand colours)
    const { hasTrend, trendData, trendOptions, partialWeeks, partialWeekSummary } =
      useWeeklyTrend(weekly, startDate, endDate, theme, chartColors)

    // Locations ranked by actual volume (all of them; the list component handles Show all)
    const locationItems = computed(() =>
      locations.value.map((loc) => ({
        id: loc.locationId || loc.id,
        label: loc.locationName || loc.name,
        value: loc.totalActualVolume ?? loc.actualVolume ?? 0,
        count: loc.totalOrders ?? loc.orders ?? 0,
      }))
    )

    // Location table
    const locationColumns = [
      { key: 'locationName', label: 'Location', sortable: true },
      { key: 'actualVolume', label: 'Actual Vol (L)', sortable: true, align: 'right' },
      { key: 'orders', label: 'Orders', sortable: true, align: 'right' },
      { key: 'completed', label: 'Completed', sortable: true, align: 'right' },
      { key: 'rate', label: 'Rate', sortable: true, align: 'right' },
    ]

    const locationTableData = computed(() => {
      return locations.value.map((loc) => ({
        locationId: loc.locationId || loc.id,
        locationName: loc.locationName || loc.name,
        actualVolume: loc.totalActualVolume ?? loc.actualVolume ?? 0,
        orders: loc.totalOrders ?? loc.orders ?? 0,
        completed: loc.completedOrders ?? loc.completed ?? 0,
        rate: loc.completionRate ?? (loc.totalOrders
          ? ((loc.completedOrders || 0) / (loc.totalOrders || 1)) * 100
          : 0),
      }))
    })

    // Order history table
    const orderColumns = [
      { key: 'id', label: 'Order ID', sortable: true },
      { key: 'requestedDate', label: 'Requested Date', sortable: true },
      { key: 'locationName', label: 'Location', sortable: true },
      { key: 'status', label: 'Status', sortable: true, align: 'center' },
      { key: 'actualVolume', label: 'Actual Volume (L)', sortable: true, align: 'right' },
    ]

    const orderTableData = computed(() => {
      return orders.value.map((order) => ({
        id: order.id,
        requestedDate: order.requestedDate,
        locationName: order.locationName,
        status: order.status,
        actualVolume: order.actualVolume ?? 0,
      }))
    })

    // Status badge classes
    const statusBadgeClass = (status) => {
      const s = (status || '').toUpperCase()
      if (s === 'COMPLETED' || s === 'DELIVERED') {
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      } else if (s === 'FAILED') {
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      } else if (s === 'CANCELLED') {
        return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
      } else {
        // REQUESTED, PLANNED, SCHEDULED, etc.
        return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-400'
      }
    }

    return {
      // State
      startDate,
      endDate,
      activeToggle,
      quickToggles,
      maxDate,
      loading,
      error,
      supplierDetail,
      // Computed
      summary,
      locations,
      supplierName,
      periodLabel,
      hasTrend,
      volSpark,
      completedSpark,
      rateSpark,
      trendData,
      trendOptions,
      partialWeeks,
      partialWeekSummary,
      locationItems,
      locationColumns,
      locationTableData,
      orderColumns,
      orderTableData,
      // Methods
      loadData,
      applyQuickRange,
      statusBadgeClass,
      formatNumber,
      formatDate,
      downloading,
      handleDownload,
    }
  },
}
</script>
